import { ObjectId } from "@novorender/api";

import { ListNode, Node, Tree } from "components/objectTree/types";
import { ModelTreeNodeData } from "features/modelTree/types";

export type GroupState = "all" | "some" | "none";

// ---------------------------------------------------------------------------
// Internal: O(n) bottom-up terminal-count caches
//
// For each (ids, tree) pair we build a lazy, memoized counter function.
// The first call for a given node path triggers a depth-first traversal
// of that subtree, caching { selected, total } terminal counts for every
// node visited along the way.  Subsequent calls for any already-visited
// path are an O(1) Map lookup — subtrees that are never queried are
// never traversed.
//
// WeakMaps ensure entries are GC-collected as soon as the key objects
// (ids, tree) are replaced or otherwise become unreachable. This relies
// on callers passing new object identities when selection or tree state
// changes.
// ---------------------------------------------------------------------------

interface TerminalCounts {
    selected: number;
    total: number;
    /** True when this node or any non-terminal descendant is directly selected. */
    hasSelectedIntermediate: boolean;
}

// ---------------------------------------------------------------------------
// Shared memoized tree-counter builder
//
// Given a tree and a visitor that computes counts for a single node (using
// a `recurse` callback for children), returns a memoized `compute(path)`
// function.  The visitor supplies the per-node counting logic; the builder
// handles memoisation and missing-node edge cases.
// ---------------------------------------------------------------------------

type CountVisitor = (node: Node<ModelTreeNodeData>, recurse: (childPath: string) => TerminalCounts) => TerminalCounts;

const EMPTY_COUNTS: TerminalCounts = { selected: 0, total: 0, hasSelectedIntermediate: false };

/** Map terminal counts to a group state. Returns undefined when total === 0 (no data). */
function countsToGroupState(counts: TerminalCounts): GroupState | undefined {
    if (counts.total <= 0) {
        return undefined;
    }
    if (counts.selected === counts.total) {
        return "all";
    }
    if (counts.selected > 0 || counts.hasSelectedIntermediate) {
        return "some";
    }
    return "none";
}

function buildMemoizedCounter(tree: Tree<ModelTreeNodeData>, visitor: CountVisitor): (path: string) => TerminalCounts {
    const memo = new Map<string, TerminalCounts>();

    function compute(path: string): TerminalCounts {
        const hit = memo.get(path);
        if (hit) {
            return hit;
        }

        const node = tree[path];
        if (!node) {
            memo.set(path, EMPTY_COUNTS);
            return EMPTY_COUNTS;
        }

        const counts = visitor(node, compute);
        memo.set(path, counts);
        return counts;
    }

    return compute;
}

// ---------------------------------------------------------------------------
// Plain (unfiltered) cache:  ids → tree → counter function
// ---------------------------------------------------------------------------

const plainCountsByIds = new WeakMap<
    Record<ObjectId, true | undefined>,
    WeakMap<Tree<ModelTreeNodeData>, (path: string) => TerminalCounts>
>();

function getPlainCounts(
    ids: Record<ObjectId, true | undefined>,
    tree: Tree<ModelTreeNodeData>,
): (path: string) => TerminalCounts {
    let byTree = plainCountsByIds.get(ids);
    if (!byTree) {
        byTree = new WeakMap();
        plainCountsByIds.set(ids, byTree);
    }

    const cached = byTree.get(tree);
    if (cached) {
        return cached;
    }

    const counter = buildMemoizedCounter(tree, (node, recurse) => {
        const selfSelected = ids[node.data.object.id] === true;
        const descendants = node.data.object.descendants;
        const hasLoadedChildren = !!node.children && node.children.length > 0;
        const hasPartialChildren =
            (node as Node<ModelTreeNodeData> & { hasMoreChildren?: boolean }).hasMoreChildren === true ||
            !!(node.data as ModelTreeNodeData & { childIterator?: unknown }).childIterator;

        // Terminal: leaf or folder whose children haven't been loaded yet.
        if (node.isLeaf || !hasLoadedChildren) {
            // For unexpanded folder nodes with a known descendants list, use it
            // to provide accurate selection counts even when the subtree hasn't
            // been loaded into the tree yet (e.g. after lasso selection).
            if (!node.isLeaf && descendants && descendants.length > 0) {
                let descSelected = 0;
                for (const descId of descendants) {
                    if (ids[descId] === true) {
                        descSelected++;
                    }
                }
                return {
                    selected: descSelected,
                    total: descendants.length,
                    hasSelectedIntermediate: selfSelected,
                };
            }

            // For non-leaf nodes without loaded children AND no descendants
            // metadata, we cannot determine the subtree state. Report zero
            // totals so countsToGroupState returns undefined (unknown) rather
            // than treating the folder's own ID as evidence of full coverage.
            // This prevents folders from incorrectly showing as fully
            // hidden/selected when only their own (non-renderable) ID is in
            // the set.
            if (!node.isLeaf) {
                return { selected: 0, total: 0, hasSelectedIntermediate: selfSelected };
            }

            return { selected: selfSelected ? 1 : 0, total: 1, hasSelectedIntermediate: false };
        }

        // Non-terminal: accumulate loaded children's terminal counts.
        let selected = 0;
        let total = 0;
        let hasSelectedIntermediate = selfSelected;
        for (const childPath of node.children!) {
            const c = recurse(childPath);
            selected += c.selected;
            total += c.total;
            hasSelectedIntermediate ||= c.hasSelectedIntermediate;
        }

        // If the node is only partially expanded (pagination / iterator),
        // the loaded child list is not the full subtree. Prefer authoritative
        // descendant metadata when available; otherwise conservatively avoid
        // reporting a fully-known total based only on loaded children.
        if (hasPartialChildren) {
            if (descendants && descendants.length > 0) {
                let descSelected = 0;
                for (const descId of descendants) {
                    if (ids[descId] === true) {
                        descSelected++;
                    }
                }
                return {
                    selected: descSelected,
                    total: descendants.length,
                    hasSelectedIntermediate,
                };
            }

            return {
                selected,
                total: total + 1,
                hasSelectedIntermediate,
            };
        }
        return { selected, total, hasSelectedIntermediate };
    });

    // Keep computation lazy: counts are memoized per requested path,
    // avoiding an unconditional full-tree traversal when the cache is built.
    byTree.set(tree, counter);
    return counter;
}

// ---------------------------------------------------------------------------
// Filtered cache:  ids → filteredObjectIds → tree → counter function
// ---------------------------------------------------------------------------

const filteredCountsByIds = new WeakMap<
    Record<ObjectId, true | undefined>,
    WeakMap<Set<ObjectId>, WeakMap<Tree<ModelTreeNodeData>, (path: string) => TerminalCounts>>
>();

function getFilteredCounts(
    ids: Record<ObjectId, true | undefined>,
    filteredObjectIds: Set<ObjectId>,
    tree: Tree<ModelTreeNodeData>,
): (path: string) => TerminalCounts {
    let byFiltered = filteredCountsByIds.get(ids);
    if (!byFiltered) {
        byFiltered = new WeakMap();
        filteredCountsByIds.set(ids, byFiltered);
    }

    let byTree = byFiltered.get(filteredObjectIds);
    if (!byTree) {
        byTree = new WeakMap();
        byFiltered.set(filteredObjectIds, byTree);
    }

    const cached = byTree.get(tree);
    if (cached) {
        return cached;
    }

    const counter = buildMemoizedCounter(tree, (node, recurse) => {
        if (!node.children || node.children.length === 0) {
            return EMPTY_COUNTS;
        }

        let selected = 0;
        let total = 0;
        let hasSelectedIntermediate = ids[node.data.object.id] === true;
        for (const childPath of node.children) {
            const child = tree[childPath];
            if (!child) {
                continue;
            }

            // The child itself counts if its id is in the filtered set.
            if (filteredObjectIds.has(child.data.object.id)) {
                total++;
                if (ids[child.data.object.id] === true) {
                    selected++;
                }
            }

            // The child's own descendants also count.
            const childCounts = recurse(childPath);
            selected += childCounts.selected;
            total += childCounts.total;
            hasSelectedIntermediate ||= childCounts.hasSelectedIntermediate;
        }
        return { selected, total, hasSelectedIntermediate };
    });

    byTree.set(tree, counter);
    return counter;
}

export function getGroupStateForIdSet(
    ids: Record<ObjectId, true | undefined>,
    item: ListNode<ModelTreeNodeData>,
    tree?: Tree<ModelTreeNodeData>,
): GroupState {
    if (item.nodeGroup) {
        let some = false;
        let all = true;
        for (const node of item.nodeGroup) {
            const inSet = ids[node.data.object.id] === true;
            some ||= inSet;
            all &&= inSet;
        }
        return all ? "all" : some ? "some" : "none";
    }

    const node = item.node;

    // For non-leaf folder nodes with loaded children, use the pre-built terminal-count
    // cache so the lookup is O(1) instead of O(subtree-size).
    if (!node.isLeaf && tree && node.children && node.children.length > 0) {
        const state = countsToGroupState(getPlainCounts(ids, tree)(node.path));
        if (state) {
            return state;
        }
        // total === 0: no terminal descendants loaded yet → fall through
    }

    if (item.nodes) {
        const selectedCount = item.nodes.reduce((acc, n) => acc + (ids[n.data.object.id] ? 1 : 0), 0);
        if (selectedCount === item.nodes.length) {
            return "all";
        }
        if (selectedCount > 0) {
            return "some";
        }
        return "none";
    }

    // For non-leaf nodes whose children aren't loaded, check known descendants
    // so that lasso / area-selection state propagates up even before expanding.
    if (!node.isLeaf) {
        // When tree is available, use the memoized counter (which already
        // handles unexpanded folders with descendants) so that repeated
        // calls are O(1) instead of O(descendants) per row/render.
        if (tree) {
            const state = countsToGroupState(getPlainCounts(ids, tree)(node.path));
            if (state) {
                return state;
            }
        } else {
            const descs = node.data.object.descendants;
            if (descs && descs.length > 0) {
                const selfSelected = ids[node.data.object.id] === true;
                let anyDescSelected = false;
                let allDescSelected = true;
                for (const descId of descs) {
                    if (ids[descId] === true) {
                        anyDescSelected = true;
                    } else {
                        allDescSelected = false;
                    }
                    if (anyDescSelected && !allDescSelected) {
                        break;
                    }
                }
                if (allDescSelected) {
                    return "all";
                }
                if (anyDescSelected || selfSelected) {
                    return "some";
                }
                return "none";
            }
        }
    }

    // For leaf nodes, the node's own ID directly represents renderable geometry
    // so its presence in the set is authoritative.
    // For non-leaf (folder) nodes with no loaded children or descendants, the
    // folder's own ID being in the set is not sufficient to determine full
    // subtree coverage — folder IDs don't have renderable geometry so hiding
    // them doesn't hide anything in 3D. Report "some" (partial/unknown) to
    // avoid incorrectly showing the EyeOff icon for visible objects.
    if (ids[node.data.object.id]) {
        return node.isLeaf ? "all" : "some";
    }
    return "none";
}

/**
 * Filter-aware version of getGroupStateForIdSet.
 * For folders, checks if all descendants in the filtered tree are selected.
 */
export function getGroupStateForFilteredTree(
    ids: Record<ObjectId, true | undefined>,
    item: ListNode<ModelTreeNodeData>,
    filteredObjectIds: Set<number>,
    tree: Tree<ModelTreeNodeData>,
): GroupState {
    // Handle node groups (similar leaf nodes grouped together)
    if (item.nodeGroup) {
        let some = false;
        let all = true;
        for (const node of item.nodeGroup) {
            const inSet = ids[node.data.object.id] === true;
            some ||= inSet;
            all &&= inSet;
        }
        return all ? "all" : some ? "some" : "none";
    }

    // Handle collapsed single-child chains
    if (item.nodes) {
        // In filtered mode, check descendants of collapsed nodes instead of just the nodes themselves
        const getFilteredCount = getFilteredCounts(ids, filteredObjectIds, tree);
        let allSelected = true;
        let someSelected = false;

        for (const n of item.nodes) {
            if (!n.isLeaf && n.children && n.children.length > 0) {
                const counts = getFilteredCount(n.path);
                if (counts.total > 0) {
                    allSelected &&= counts.selected === counts.total;
                    someSelected ||= counts.selected > 0 || counts.hasSelectedIntermediate;
                    continue;
                }
            }

            // Leaf or no filtered descendants in this node — check the node itself.
            const nodeSelected = ids[n.data.object.id] === true;
            allSelected &&= nodeSelected;
            someSelected ||= nodeSelected;
        }

        return allSelected ? "all" : someSelected ? "some" : "none";
    }

    // For folders with children, check all descendants in the filtered tree
    const node = item.node;
    if (!node.isLeaf && node.children && node.children.length > 0) {
        const counts = getFilteredCounts(ids, filteredObjectIds, tree)(node.path);
        if (counts.total === 0) {
            // No filtered descendants — just check the node itself
            return ids[node.data.object.id] ? "all" : "none";
        }

        return countsToGroupState(counts) ?? "none";
    }

    // Leaf node - just check itself
    return ids[item.node.data.object.id] ? "all" : "none";
}

/**
 * Recursively collects all descendant IDs that are in the filtered set.
 */
export function collectDescendantIds(
    node: Node<ModelTreeNodeData>,
    tree: Tree<ModelTreeNodeData>,
    filteredObjectIds: Set<number>,
): number[] {
    const result: number[] = [];

    function traverse(currentNode: Node<ModelTreeNodeData>) {
        if (!currentNode.children) {
            return;
        }

        for (const childPath of currentNode.children) {
            const child = tree[childPath];
            if (!child) {
                continue;
            }

            const childId = child.data.object.id;
            if (filteredObjectIds.has(childId)) {
                result.push(childId);
            }

            // Recursively check this child's descendants
            traverse(child);
        }
    }

    traverse(node);
    return result;
}
